import { useRef, useEffect } from "react";
import InputMask from 'react-input-mask';
import { Controller } from "react-hook-form";

export default function FormInput({
  id,
  label,
  description,
  descriptionLocation="after",
  maxLength,
  type,
  register,
  control,
  error,
  required,
  options,
  rules,
  mask,
  readonly=false,
  preventPaste=false,
  setValue
}) {
  const maxFiles = 3;
  const maxFileSize = 10 * 1024 * 1024; //10MB
  // creat ref for input element
  const maskInputRef = useRef(null);

  const GenericLabel = () => (
    <label
      htmlFor={id}
      className={`form-item__label ${required && "form-required"}`}
    >
      {label}
    </label>
  );

  const handleFileChange = (event, files) => {
    let newFiles = Array.from(event.target.files);
    event.target.value = ''; // clear input value incase we remove and try to re-add same file
    // check for max files
    if (files.length + newFiles.length > maxFiles) {
      alert(`You can only select up to ${maxFiles} files.`);
      return files;
    }
    // check for existing files with same filename and filesize
    for (const newFile of newFiles) {
      if(files.find(file => file.name == newFile.name)){
        newFiles = newFiles.filter(filterFile => filterFile.name !== newFile.name);
      }
      if(newFile.size > maxFileSize){
        alert(`${newFile.name} is bigger than the max file size of 10MB`);
        return files;
      }
    }
    
    return [...files, ...newFiles];
  };

  const handleRemoveFile = (fileToRemove, files) => {
    const updatedFiles = files.filter(file => file !== fileToRemove);
    return updatedFiles;
  };

  // Handlers to prevent copy, paste, and cut actions
  const handlePreventCopyPaste = (e) => {
    e.preventDefault();
  };

// Trigger a manual change event on load to apply mask to form state
useEffect(() => {
  if (maskInputRef.current) {
    setValue(maskInputRef.current.name, maskInputRef.current.value);
  }
}, [maskInputRef]);

  return (
    <>     
      {type === "text" && (
        <div className={`js-form-item form-item js-form-type-textfield form-type-textfield ${readonly ? 'webform-readonly' : ''}`}>
          {label && <GenericLabel />}
          {description && descriptionLocation === "before" && (
            <div className="description">
              <div className="webform-element-description">{description}</div>
            </div>
          )}
          {mask ? (
            <Controller
              control={control}
              name={id}
              render={({ field }) => (
                <InputMask
                  {...field}
                  mask={mask}
                >
                  { (inputProps) => <input {...inputProps} ref={maskInputRef} className="js-webform-input-mask form-text form-element form-element--type-text form-element--api-textfield" autoComplete="nosir" /> }
                </InputMask>
              )}
            />
          ) : (
            <input
              id={id}
              type={type || "text"}
              className="js-webform-input-mask form-text form-element form-element--type-text form-element--api-textfield"
              {...register}
              autoComplete="off"
              readOnly={readonly}
              onPaste={preventPaste ? handlePreventCopyPaste : undefined}
              maxLength={maxLength}
            />
          )}
          {description && descriptionLocation === "after" && (
            <div className="description">
              <div className="webform-element-description">{description}</div>
            </div>
          )}
          {error && <div className="error"> {error.message}</div>}
        </div>
      )}

      {type === "select" && (
        <div className="js-form-item form-item js-form-type-select form-type-select">
          {label && <GenericLabel />}
          {description && descriptionLocation === "before" && (
            <div className="description">
              <div className="webform-element-description">{description}</div>
            </div>
          )}
          <select
            id={id}
            type={type || "text"}
            className="form-select form-element form-element--type-select"
            {...register}
            autoComplete="nosir"
          >
            {options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {description && descriptionLocation === "after" && (
            <div className="description">
              <div className="webform-element-description">{description}</div>
            </div>
          )}
          {error && <div className="error"> {error.message}</div>}
        </div>
      )}

      {type === "radio" && (
        <fieldset
          className="radios--wrapper fieldgroup form-composite webform-composite-visible-title js-webform-type-radios webform-type-radios js-form-item form-item js-form-wrapper form-wrapper"
          role="radiogroup"
        >
          <legend>{label && <GenericLabel />}</legend>
          <div className="fieldset-wrapper">
            <div className="js-webform-radios webform-options-display-one-column form-radios">
              {options.map((option, index) => (
                <div
                  className="js-form-item form-item js-form-type-radio form-type-radio js-form-item-iam form-item-iam"
                  key={index}
                >
                  <input
                    type="radio"
                    id={`${id}-${option.value}`}
                    value={option.value}
                    className="form-radio form-boolean form-boolean--type-radio"
                    {...register}
                  />

                  <label
                    htmlFor={`${id}-${option.value}`}
                    className="form-item__label option"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {error && <div className="error"> {error.message}</div>}
          </div>
        </fieldset>
      )}

      {type === "textarea" && (
        <div className="js-form-item form-item js-form-type-textarea form-type-textarea">
          {label && <GenericLabel />}
          {description && descriptionLocation === "before" && (
              <div className="description">
                <div className="webform-element-description">{description}</div>
              </div>
            )}
          <div className="form-textarea-wrapper">
            <textarea
              id={id}
              rows="5"
              cols="60"
              className="form-textarea form-element form-element--type-textarea form-element--api-textarea resize-vertical"
              {...register}
              autoComplete="nosir"
              maxLength={maxLength}
            ></textarea>
            {description && descriptionLocation === "after" && (
              <div className="description">
                <div className="webform-element-description">{description}</div>
              </div>
            )}
            {maxLength && (
              <div className="description">
                <div className="webform-element-description">{maxLength} character limit</div>
              </div>
            )}
            {error && <div className="error"> {error.message}</div>}
          </div>
        </div>
      )}

      {type === "checkbox" && (
        <>
          <div className="js-form-item form-item form-type-checkbox">
          {description && descriptionLocation === "before" && (
              <div className="description">
                <div className="webform-element-description">{description}</div>
              </div>
            )}
            <input
              type="checkbox"
              id={id}
              className="form-checkbox form-boolean form-boolean--type-checkbox"
              {...register}
            />
            <label
              htmlFor={id}
              className={`form-item__label option ${
                required && "form-required"
              }`}
            >
              {label}
            </label>
            {description && descriptionLocation === "after" && (
              <div className="description">
                <div className="webform-element-description">{description}</div>
              </div>
            )}
            {error && <div className="error"> {error.message}</div>}
          </div>
        </>
      )}

      {type === "file" && (
        <div className="js-form-item form-item js-form-type-managed-file form-type-managed-file js-form-item-test-file form-item-test-file">
          <label
            htmlFor={id}
            className={`form-item__label option ${required && "form-required"}`}
          >
            {label}
          </label>
          <Controller
            control={control}
            name={id}
            defaultValue={[]}
            rules={rules}
            render={({ field: { onChange, onBlur, value } }) => (
              <div className="form-element">
                <div className="webform-flexbox js-webform-flexbox" style={{margin:0, alignItems: 'center'}}>
                {description && descriptionLocation === "before" && (
                    <div className="description">
                      <div className="webform-element-description">{description}</div>
                    </div>
                  )}
                  <button
                    className="webform-button--submit button button--primary js-form-submit form-submit"
                    style={{marginTop:0, marginBottom: 0}}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(id).click();
                    }}
                  >
                    Select File(s)
                  </button>
                  {description && descriptionLocation === "after" && (
                    <div className="description">
                      <div className="webform-element-description">{description}</div>
                    </div>
                  )}
                </div>
                  <input
                    id={id}
                    type="file"
                    onChange={(e) => onChange(handleFileChange(e, value))}
                    multiple={true}
                    style={{ display: "none" }}
                    accept=".pdf, .jpg, .jpeg, .doc, .docx, .heic"
                  /> 
                { value.length > 0 && (
                  <div className="file-list">
                    <label className="form-item__label">Selected Files:</label>
                    <ul>
                      {value.map((file, index) => (
                        <li key={index}>
                          {file.name}
                          <button
                            className="remove"
                            onClick={() =>
                              onChange(handleRemoveFile(file, value))
                            }
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          />
          
          {error && <div className="error"> {error.message}</div>}
        </div>
      )}
    </>
  );
}